// eSCR (eCourts High Court Judgements & Orders) backend API helpers.
//
// Talks to the `ejudgement-hc` Cloud Run service (ValscoTech/eJudgement-HC),
// which scrapes judgments.ecourts.gov.in/pdfsearch. The server handles the
// captcha (Tesseract OCR), session and pagination internally — the client just
// sends { keyword, searchType } and gets the full result set back.
//
// Override the base URL with REACT_APP_ESCR_API_URL for local dev.

export const ESCR_API_BASE_URL =
  process.env.REACT_APP_ESCR_API_URL ||
  "https://ejudgement-hc-596718606544.asia-south1.run.app";

const JSON_HEADERS = { "Content-Type": "application/json" };

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
}

/**
 * Run a keyword search. The server auto-solves the captcha and returns ALL
 * matching judgments (no captcha / pagination params needed).
 * @param {object} p
 * @param {string} p.keyword
 * @param {string} [p.searchType="PHRASE"]  PHRASE | ANY | ALL
 * @returns {Promise<{ totalRecords: number, results: Array }>}
 */
export async function searchHighCourt(p) {
  const res = await fetch(`${ESCR_API_BASE_URL}/api/v1/highcourt/search`, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({
      keyword: (p.keyword || "").trim(),
      searchType: p.searchType || "PHRASE",
    }),
  });
  const body = await parseJsonSafe(res);
  if (!res.ok || !body || !body.success) {
    const err = new Error((body && (body.error || body.message)) || "Search failed");
    err.status = res.status;
    throw err;
  }
  return {
    totalRecords: body.data?.totalRecords ?? (body.data?.results || []).length,
    results: body.data?.results || [],
  };
}

/** Absolute URL for a judgment PDF (result.pdfUrl is a relative backend path). */
export function pdfUrlFor(result) {
  if (!result || !result.pdfUrl) return null;
  return result.pdfUrl.startsWith("http") ? result.pdfUrl : `${ESCR_API_BASE_URL}${result.pdfUrl}`;
}

/** Fetch a judgment PDF as a Blob (for in-app modal viewing). */
export async function fetchHighCourtPdf(result) {
  const url = pdfUrlFor(result);
  if (!url) throw new Error("No PDF available for this judgment");
  const res = await fetch(url);
  if (!res.ok) {
    const body = await parseJsonSafe(res);
    throw new Error((body && (body.error || body.message)) || "Failed to open judgment PDF");
  }
  return await res.blob();
}
